// ----------------------------------------------------------------------------
// Dependencies
// ----------------------------------------------------------------------------
import React, { useState, useMemo, useCallback , useEffect, useRef } from 'react';
import {
  Button,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow
} from '@mui/material';
import styled from 'styled-components';
import { PlusOutlined } from '@ant-design/icons';
import {
  Button as AntButton,
  Card,
  Col,
  Input,
  Modal as AntModal,
  Row
} from 'antd';
import PasswordRevealInline from 'components/PasswordRevealInline';
// import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import COLOR from 'utils/constants/color';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
// import { useCurrentOrg } from 'store/organizations/selectors';
import { handleEnableDiableUser, handleUpateUserSubRole } from 'store/users/thunks';
import { openLoginInNewWindow } from 'store/keystore/thunks';
import Login_as from "features/forms/admin/Login_as";
import Loading from 'components/Loading';
import { handleGetDistrictAdminForOrg } from 'store/organizations/thunks';
import SpinLoader from 'components/Loader/SpinLoader';
import { useCurrentOrg } from 'store/organizations/selectors';
import AntdDropdown from 'components/UI/AntdDropdown';
import usePermissionForUser from 'components/hooks/permissions/usePermissionForUser';

// ----------------------------------------------------------------------------
// Styled components
// ----------------------------------------------------------------------------
const Wrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const DetailModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: ${COLOR.white};
  border-radius: 3px;
  padding: 16px;
`;

const ConfirmModalContent = styled(DetailModalContent)``;

const SectionTitle = styled.p`
  color: black;
  font-size: 17px;
  font-weight: 400;
`;

const Highlight = styled.span`
  font-weight: 600;
`;

const ActionBar = styled(Row)`
  margin-bottom: 16px;
`;

const extractInParens = (s) => {
  const m = s.match(/\(([^)]+)\)/);
  return m ? m[1] : '-';
};

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------
export default function DistrictsAdminTable({
  userloading,
  active_tab,
  from='',
  allowAdd = true
}) {
  const {isUserCoach} = usePermissionForUser();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [pageInfo, setPageInfo] = useState({ page: 0, rowsPerPage: 100 });
  const [detailUser, setDetailUser] = useState(null);
  const [confirmUser, setConfirmUser] = useState(null);
  const [loadingActionId, setLoadingActionId] = useState(null);
  const [data_loading, setDataLoading] = useState(false);
  const [total_count, setToatalCount] = useState(0);
  const [districtAdmin, SetAllDistrictAdmin] = useState([])
  const org = useCurrentOrg()
  const prevOrgIdRef = useRef(org?.id);
  async function getParentInfo() {
    try {
      setDataLoading(true)
      const response = await dispatch(handleGetDistrictAdminForOrg(pageInfo.page*pageInfo.rowsPerPage, pageInfo.rowsPerPage, org.id , from ==="district_admin"?"placement_admin":"admin"))
      if (response && response.success) {
        const { total_count, district_admins } = response.data || {};
        setToatalCount(total_count)
        SetAllDistrictAdmin(district_admins)
      }
    } catch (error) {

    } finally {
      setDataLoading(false)
    }

  }
  useEffect(() => {
    if (org && org.id) {
      if (active_tab !== "district_admins" && active_tab !== "district_admins_view") return;
      getParentInfo()
    }
  }, [org, pageInfo, active_tab])

  useEffect(() => {
    if (active_tab !== "district_admins" && active_tab !== "district_admins_view") return;
    if (prevOrgIdRef.current !== undefined && prevOrgIdRef.current !== org?.id) {
      setSearchText('');
    }
    prevOrgIdRef.current = org?.id;
  }, [org?.id, active_tab])

  const filteredTeachers = useMemo(() =>sortBy(districtAdmin,['firstName', 'lastName']),[districtAdmin]);

  // Derived: after search
  const displayed = useMemo(() => {
    if (searchText.length === 0) return filteredTeachers;
    const q = searchText.toLowerCase();
    return sortBy(
      filteredTeachers.filter(
        (u) =>
          `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
          u.role.toLowerCase().includes(q)
      ),
      ['firstName', 'lastName']
    );
  }, [filteredTeachers, searchText]);

  // Current page slice
  const pageRows = useMemo(() => {
    return displayed
  }, [displayed, pageInfo]);

  // Handlers
  const onSearch = useCallback((e) => {
    setSearchText(e.target.value);
  }, []);

  const onPageChange = useCallback((_, newPage) => {
    setPageInfo((p) => ({ ...p, page: newPage }));
  }, []);

  const onRowsPerPageChange = useCallback((e) => {
    setPageInfo({ page: 0, rowsPerPage: parseInt(e.target.value, 10) });
  }, []);

  const openDetail = useCallback((user) => setDetailUser(user), []);
  const closeDetail = useCallback(() => setDetailUser(null), []);

  const openConfirm = useCallback((user) => setConfirmUser(user), []);
  const closeConfirm = useCallback(() => setConfirmUser(null), []);

  const handleToggleActive = useCallback(async () => {
    if (!confirmUser) return;
    setLoadingActionId(confirmUser.id);
    const response  = await dispatch(
      handleEnableDiableUser(confirmUser.id, !confirmUser.active)
    );
    if(response){
      SetAllDistrictAdmin((prev) => {
        return prev?.map((element) => {
          if (element.id === response.id) {
            return { ...element, active: !confirmUser.active };
          }
          else{
            return element
          }
        })
      })
    }
    setLoadingActionId(null);
    closeConfirm();
  }, [dispatch, confirmUser, closeConfirm]);

  const onAddUser = useCallback(
    (isEdit = false) => {
      navigate('/add-users', {
        state: {
          userData: isEdit ? detailUser : { role:from=="district_admin"?"placement_admin":"admin"},
          isEdit
        }
      });
    },
    [navigate, detailUser]
  );

  const loginAs = useCallback(async () => {
    if (!detailUser) return;
    setLoadingActionId(detailUser.id);
    await dispatch(openLoginInNewWindow(detailUser.id));
    setLoadingActionId(null);
  }, [dispatch, detailUser]);

  const baseColumns = [
    { label: 'First Name', render: (u) => u.firstName },
    { label: 'Last Name', render: (u) => u.lastName },
    { label: 'Username', render: (u) => u.username },
    { label: 'Role', render: (u) => u.role },
    { label: 'Password', render: (u) => <PasswordRevealInline userId={u.id} username={u.username} /> },
    { label: 'Sub-role', render: (u) => u.user_sub_roles ? u.user_sub_roles : '-' },
  ];

  const statusColumn = {
    label: 'Status',
    render: (u) => (
      <Button
        variant="contained"
        color={u.active ? 'primary' : 'secondary'}
        sx={{ fontSize: 12, width: 80 }}
        onClick={(e) => {
          e.stopPropagation();
          openConfirm(u);
        }}
        disabled={isUserCoach}
      >
        {loadingActionId === u.id ? (
          <Loading size={18} white />
        ) : u.active ? 'Enabled' : 'Disabled'}
      </Button>
    )
  };

  const columns = allowAdd ? [...baseColumns, statusColumn] : baseColumns;

  const handleClickSubRole = async (subRole, user) => {
    try {
      setDataLoading(true);
      const response = await dispatch(
        handleUpateUserSubRole({ userId: user?.id, user_sub_roles: subRole }),
      );
      setDetailUser(response);
      if (response) {
        SetAllDistrictAdmin((prev) => {
          return prev?.map((element) => {
            if (element.id === response.id) {
              return { ...element, user_sub_roles: subRole };
            } else {
              return element;
            }
          });
        });
      }
      setDataLoading(false);
    } catch (error) {
      console.log(error);
    }
  };

  const menuNav = useCallback((user) => {
    const menu = [
      {
      key: "",
      label: "Unselect",
      onClick: () => handleClickSubRole("", user)
    }
    ,{
      key: "coach",
      label: "coach",
      onClick: () => handleClickSubRole("coach", user)
    }];

    return {
      items: menu,
    };
  }, [districtAdmin]);

  const RenderChangeUserSubRole = ({ user }) => {
    return (
      <AntdDropdown menuNav={menuNav(user)}>
        {user?.user_sub_roles ? user?.user_sub_roles : 'User Sub Role'}
      </AntdDropdown>
    );
  };

  return (
    <>
      {/* Detail Modal */}
      <SpinLoader dataloader={data_loading}/>
      <AntModal
        title={get(detailUser, 'displayName', 'User Details')}
        centered
        open={!!detailUser}
        footer={null}
        onCancel={closeDetail}
        width="60%"
      >
        <Row justify="space-between" align="middle">
          <div className='flex items-center gap-10px'>
            {isUserCoach ? null : (allowAdd ? <AntButton onClick={() => onAddUser(true)}>
              Edit Profile
          </AntButton> : null)}
          {detailUser?.role === "admin" && (
            <RenderChangeUserSubRole user={detailUser} />
          )}
          </div>
          <Login_as user={detailUser} />
        </Row>

        <Card type="inner" title="Detail View" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <p><strong>First name:</strong> {get(detailUser, 'firstName', '')}</p>
              <p><strong>Username:</strong> {get(detailUser, 'username', '')}</p>
              <p><strong>Role:</strong>     {get(detailUser, 'role', '')}</p>
            </Col>
            <Col span={12}>
              <p><strong>Last name:</strong> {get(detailUser, 'lastName', '')}</p>
              <p><strong>Password:</strong>  <PasswordRevealInline userId={detailUser?.id} username={detailUser?.username} /></p>
            </Col>
          </Row>
        </Card>
      </AntModal>

      {/* Confirm enable/disable */}
      <Modal open={!!confirmUser} onClose={closeConfirm}>
        <ConfirmModalContent>
          Are you sure you want to{' '}
          {confirmUser?.active ? 'disable' : 'enable'}{' '}
          {get(confirmUser, 'firstName', '')}{' '}
          {get(confirmUser, 'lastName', '')}?
          <Row justify="center" sx={{ mt: 2 }}>
            <Button sx={{ mr: 1 }} onClick={closeConfirm}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleToggleActive}
              disabled={loadingActionId === confirmUser?.id}
            >
              {loadingActionId === confirmUser?.id
                ? <Loading size={20} white />
                : 'Yes'}
            </Button>
          </Row>
        </ConfirmModalContent>
      </Modal>

      {/* Main table & toolbar */}
      <Wrapper>
        <ActionBar>
          <Col>
          {isUserCoach ? null : (allowAdd ? (
            <AntButton
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => onAddUser(false)}
            >
             {from=="district_admin"?"Add Placement Admin":"Add District Admin"}
            </AntButton>

          ) : null)}
          </Col>
          <Col flex="auto" />
          <Col>
            <Input
              placeholder="Search District Admins by name"
              value={searchText}
              onChange={onSearch}
              style={{ width: 250 }}
            />
          </Col>
        </ActionBar>

        <SectionTitle>
          View <Highlight>District Admin</Highlight> Records.
        </SectionTitle>

        <TableContainer>
          <Table stickyHeader sx={{ backgroundColor: '#fff' }}>
            <TableHead>
              <TableRow>
                {columns.map((col, i) => (
                  <TableCell
                    key={i}
                    sx={{ fontSize: 16, fontWeight: 600, textAlign: 'left' }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {isEmpty(displayed) ? (
                userloading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      <Loading size={30} />
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      No results
                    </TableCell>
                  </TableRow>
                )
              ) : (
                pageRows.map((u) => (
                  <TableRow
                    key={u.id}
                    hover
                    onClick={() => openDetail(u)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {columns.map((col, j) => (
                      <TableCell key={j} sx={{ textAlign: 'left' }}>
                        {col.render ? col.render(u) : get(u, col.field, '-')}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>

            <TableFooter>
              <TableRow>
                {!isEmpty(displayed) && (
                  <TablePagination
                    count={total_count}
                    page={pageInfo.page}
                    rowsPerPage={pageInfo.rowsPerPage}
                    onPageChange={onPageChange}
                    onRowsPerPageChange={onRowsPerPageChange}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    backIconButtonProps={{ color: 'secondary' }}
                    nextIconButtonProps={{ color: 'secondary' }}
                  />
                )}
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Wrapper>
    </>
  );
}